import { useEffect, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, LayoutDashboard, ShoppingBag, Users, Wallet, Settings, Share2, LogOut } from "lucide-react";
import PortalChatWidget from "./PortalChatWidget";
import InstallAppButton from "../../pwa/InstallAppButton";
import { registerPortalSW } from "../../pwa/registerSW";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const portalApi = axios.create({ baseURL: API, withCredentials: true });

portalApi.interceptors.request.use((cfg) => {
  const m = (cfg.method || "get").toUpperCase();
  if (["POST", "PATCH", "PUT", "DELETE"].includes(m)) {
    const tok = (document.cookie || "").split(";").map((s) => s.trim()).find((s) => s.startsWith("csrf_token="));
    if (tok) cfg.headers["X-CSRF-Token"] = decodeURIComponent(tok.split("=")[1]);
  }
  return cfg;
});

export const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const PortalCtx = createContext({ profile: null, refresh: () => {} });
export const usePortal = () => useContext(PortalCtx);

const NAV = [
  { to: "/affiliate", label: "Dashboard", icon: LayoutDashboard, testId: "portal-nav-dashboard", end: true },
  { to: "/affiliate/share", label: "Share", icon: Share2, testId: "portal-nav-share" },
  { to: "/affiliate/orders", label: "Orders", icon: ShoppingBag, testId: "portal-nav-orders" },
  { to: "/affiliate/referrals", label: "Referrals", icon: Users, testId: "portal-nav-referrals" },
  { to: "/affiliate/payouts", label: "Payouts", icon: Wallet, testId: "portal-nav-payouts" },
  { to: "/affiliate/settings", label: "Settings", icon: Settings, testId: "portal-nav-settings" },
];

export default function PortalShell() {
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  const refresh = () => {
    portalApi.get("/affiliate/me")
      .then((r) => setProfile(r.data.profile))
      .catch((e) => {
        if (e?.response?.status === 401) { nav("/affiliate/login"); return; }
        setErr(e?.response?.data?.detail || "Failed to load");
      });
  };
  useEffect(refresh, [nav]);

  // Register the offline shell service worker once the portal mounts.
  useEffect(() => { registerPortalSW(); }, []);

  const logout = async () => {
    try { await portalApi.post("/affiliate/auth/logout"); } catch { /* ignore */ }
    try { localStorage.removeItem("fuelled.portal.remembered_email"); } catch {}
    toast.success("Signed out");
    nav("/affiliate/login");
  };

  if (err) return <div className="min-h-screen bg-neutral-950 text-red-400 flex items-center justify-center px-6 text-center">{err}</div>;
  if (!profile) return <div className="min-h-screen bg-neutral-950 text-neutral-500 flex items-center justify-center">Loading…</div>;

  return (
    <PortalCtx.Provider value={{ profile, refresh }}>
      <div className="min-h-screen bg-neutral-950 text-neutral-100 flex">
        {/* Sidebar — desktop only */}
        <aside data-testid="portal-sidebar" className="hidden lg:flex flex-col w-64 shrink-0 border-r border-neutral-900 bg-neutral-950 sticky top-0 h-screen">
          <div className="h-16 flex items-center gap-2 px-6 border-b border-neutral-900">
            <div className="h-9 w-9 rounded-xl bg-orange-500 flex items-center justify-center">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-sm font-bold tracking-tight">Fuelled Portal</div>
              <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">Affiliate</div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-neutral-900 text-white" : "text-neutral-400 hover:text-neutral-100 hover:bg-neutral-900/50"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-neutral-900">
            <InstallAppButton variant="sidebar" />
            <div className="rounded-xl bg-neutral-900 px-3 py-2.5 mb-2">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Signed in as</div>
              <div className="text-sm font-medium truncate mt-0.5">{profile.name}</div>
              <div className="text-[10px] text-neutral-500 truncate">{profile.tier}</div>
            </div>
            <button
              onClick={logout}
              data-testid="affiliate-logout"
              className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-xs uppercase tracking-widest text-neutral-500 hover:text-red-400 hover:bg-neutral-900/50"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </aside>

        {/* Content column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile top bar */}
          <div className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-neutral-950/80 border-b border-neutral-900">
            <div className="px-4 h-14 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-xl bg-orange-500 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
                </div>
                <div className="text-sm font-bold tracking-tight">Fuelled</div>
              </div>
              <button onClick={logout} data-testid="affiliate-logout-mobile" className="text-[11px] uppercase tracking-widest text-neutral-500 hover:text-neutral-200 flex items-center gap-1">
                <LogOut className="h-3.5 w-3.5" /> Sign out
              </button>
            </div>
            <div className="px-4 pb-2 flex justify-end">
              <InstallAppButton variant="compact" />
            </div>
          </div>

          <main className="flex-1 pb-24 lg:pb-8">
            <Outlet />
          </main>

          {/* Floating AI Copilot widget — bottom-right on every portal page */}
          <PortalChatWidget />

          {/* Bottom nav — mobile only */}
          <nav data-testid="portal-bottom-nav" className="lg:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-neutral-900 bg-neutral-950/95 backdrop-blur-xl">
            <div className="grid grid-cols-6">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`${item.testId}-mobile`}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-2 gap-0.5 text-[9px] uppercase tracking-widest transition-colors ${
                      isActive ? "text-orange-400" : "text-neutral-500 hover:text-neutral-200"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  <span className="truncate max-w-full px-1">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </PortalCtx.Provider>
  );
}

export function Page({ title, subtitle, actions, children, testId }) {
  return (
    <div data-testid={testId} className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">{title}</h1>
          {subtitle && <div className="text-sm text-neutral-500 mt-1">{subtitle}</div>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, sub, testId, highlight = false }) {
  const cls = highlight
    ? "rounded-2xl border border-orange-500/40 p-4 bg-gradient-to-br from-orange-600/25 via-orange-500/10 to-orange-400/5 shadow-[0_0_0_1px_rgba(249,115,22,0.15),0_10px_40px_-15px_rgba(249,115,22,0.6)]"
    : "rounded-2xl bg-neutral-900 border border-neutral-800 p-4";
  const labelCls = highlight
    ? "text-[10px] uppercase tracking-widest text-orange-200/90 flex items-center gap-1"
    : "text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1";
  const valueCls = highlight
    ? "text-xl lg:text-2xl font-semibold mt-2 text-white"
    : "text-xl lg:text-2xl font-semibold mt-2";
  return (
    <div data-testid={testId} className={cls}>
      <div className={labelCls}>
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={valueCls}>{value}</div>
      {sub && <div className={highlight ? "text-[11px] text-orange-200/70 mt-1" : "text-[11px] text-neutral-500 mt-1"}>{sub}</div>}
    </div>
  );
}
